import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, School, CalendarClock, Activity, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const LOGO = "https://e7.pngegg.com/pngimages/1016/955/png-clipart-mandala-logo-quran-illustration-text-logo-thumbnail.png";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/pesantren", label: "Objek Mad’u", icon: School, testid: "nav-pesantren" },
  { to: "/kunjungan", label: "Kunjungan", icon: CalendarClock, testid: "nav-kunjungan" },
  { to: "/audit", label: "Audit", icon: Activity, testid: "nav-audit" },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings, testid: "nav-pengaturan" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white text-neutral-950 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 border-r border-neutral-200 bg-white sticky top-0 h-screen">
        <div className="px-6 py-8 border-b border-neutral-200 flex items-center gap-3">
          <img src={LOGO} alt="Rabtah" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-display font-black text-lg leading-none tracking-tight">RABTAH</div>
            <div className="label-overline mt-1">Data Center</div>
          </div>
        </div>
        <nav className="flex-1 py-6">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors duration-150 ${
                  isActive
                    ? "border-black bg-neutral-50 font-semibold"
                    : "border-transparent text-neutral-600 hover:bg-neutral-50 hover:text-black"
                }`
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-neutral-200">
          <div className="text-xs text-neutral-500 mb-2 truncate" data-testid="user-email-sidebar">{user?.email}</div>
          <button
            data-testid="logout-btn-sidebar"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 border border-black py-2 text-sm font-medium hover:bg-black hover:text-white transition-colors duration-150"
          >
            <LogOut size={14} />
            Keluar
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-40 bg-white border-b border-neutral-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="Rabtah" className="w-8 h-8 object-contain" />
            <div>
              <div className="font-display font-black text-sm tracking-tight leading-none">RABTAH</div>
              <div className="label-overline text-[0.6rem] mt-0.5">Data Center</div>
            </div>
          </div>
          <button
            data-testid="logout-btn-mobile"
            onClick={handleLogout}
            aria-label="Keluar"
            className="p-2 border border-black hover:bg-black hover:text-white transition-colors duration-150"
          >
            <LogOut size={14} />
          </button>
        </header>

        <main className="flex-1 pb-20 md:pb-0 flex flex-col">
          <div className="flex-1">
            <Outlet />
          </div>
          <footer
            data-testid="app-footer"
            className="border-t border-neutral-200 px-6 py-4 text-center mt-8"
          >
            <div className="label-overline text-neutral-500">
              © 2026 PT. Cloud Rise Technology
            </div>
          </footer>
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-200 grid grid-cols-5">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 gap-1 border-t-2 transition-colors duration-150 ${
                  isActive
                    ? "border-black text-black font-semibold"
                    : "border-transparent text-neutral-500"
                }`
              }
            >
              <item.icon size={20} />
              <span className="text-[0.65rem]">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
      <Toaster position="top-right" />
    </div>
  );
}
